import React, { useState } from "react";
import { trpc } from "@/lib/trpc";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Plus, Trash2, Loader2, Box, Sparkles, Download } from "lucide-react";
import { exportToExcel } from "@/lib/excel.utils";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function BoxPresetConfigTab() {
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [editingPreset, setEditingPreset] = useState<any>(null);
  const [formData, setFormData] = useState({
    code: "",
    name: "",
    standard: "ECMA",
    length: 100,
    width: 60,
    height: 150,
    formulaDesc: "",
  });

  const { data: presets = [], isLoading } = useQuery(
    trpc.estimating.getBoxPresets.queryOptions()
  );

  const openCreateModal = () => {
    setEditingPreset(null);
    setFormData({
      code: "ECMA_NEW_" + Date.now().toString().slice(-4),
      name: "Mẫu Hộp Mới",
      standard: "ECMA",
      length: 100,
      width: 60,
      height: 150,
      formulaDesc: "W = 2*(L+W) + 16.6; L = H + W + 25",
    });
    setIsOpen(true);
  };

  const openEditModal = (preset: any) => {
    setEditingPreset(preset);
    setFormData({
      code: preset.code,
      name: preset.name,
      standard: preset.standard || "ECMA",
      length: preset.defaultDimensions?.length || 100,
      width: preset.defaultDimensions?.width || 60,
      height: preset.defaultDimensions?.height || 150,
      formulaDesc: preset.formulaDesc || "",
    });
    setIsOpen(true);
  };

  const handleSave = () => {
    toast.success("Đã lưu mẫu hộp chuẩn: " + formData.name);
    setIsOpen(false);
    queryClient.invalidateQueries({ queryKey: trpc.estimating.getBoxPresets.queryKey() });
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end items-center gap-2">
        <Button
          size="sm"
          variant="outline"
          className="h-8 text-xs font-semibold"
          onClick={() =>
            exportToExcel(
              (presets as any[]).map((p: any) => ({
                ...p,
                dims: p.defaultDimensions ? `${p.defaultDimensions.length} x ${p.defaultDimensions.width} x ${p.defaultDimensions.height}` : "",
              })),
              [
                { key: "code", label: "Mã mẫu", width: 14 },
                { key: "name", label: "Tên mẫu bao bì", width: 30 },
                { key: "standard", label: "Tiêu chuẩn", width: 12 },
                { key: "dims", label: "Dài x Rộng x Cao (mm)", width: 22 },
                { key: "formulaDesc", label: "Công thức trải phẳng", width: 40 },
              ],
              "mau_hop_chuan",
            )
          }
        >
          <Download className="h-3.5 w-3.5 mr-1" /> Xuất Excel
        </Button>
        <Button size="sm" onClick={openCreateModal} className="h-8 text-xs font-semibold bg-indigo-600 hover:bg-indigo-700">
          <Plus className="h-3.5 w-3.5 mr-1" />
          Thêm Mẫu Mới
        </Button>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden shadow-xs">
        <Table className="text-xs">
          <TableHeader>
            <TableRow className="bg-slate-50/80">
              <TableHead className="font-bold">Mã Mẫu</TableHead>
              <TableHead className="font-bold">Tên Mẫu Bao Bì</TableHead>
              <TableHead className="font-bold">Tiêu Chuẩn</TableHead>
              <TableHead className="font-bold text-center">Dài x Rộng x Cao (mm)</TableHead>
              <TableHead className="font-bold">Công Thức Trải Phẳng</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-6">
                  <Loader2 className="h-5 w-5 animate-spin mx-auto text-indigo-600" />
                </TableCell>
              </TableRow>
            ) : presets.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-6 text-slate-500">
                  Chưa có cấu hình mẫu bao bì
                </TableCell>
              </TableRow>
            ) : (
              (presets as any[]).map((p: any) => (
                <TableRow key={p.code} className="hover:bg-slate-50/60 cursor-pointer" onClick={() => openEditModal(p)}>
                  <TableCell className="font-mono font-bold text-indigo-700">
                    {p.code}
                  </TableCell>
                  <TableCell className="font-medium text-slate-800">
                    {p.name}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={p.standard === 'ECMA' ? 'bg-blue-50 text-blue-700 border-blue-200 text-[10px]' : 'bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]'}>
                      {p.standard}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-center font-mono font-semibold">
                    {p.defaultDimensions ? `${p.defaultDimensions.length} x ${p.defaultDimensions.width} x ${p.defaultDimensions.height}` : '—'}
                  </TableCell>
                  <TableCell className="text-slate-600 font-mono text-[11px] max-w-xs truncate">
                    {p.formulaDesc || 'Tự động tính theo tỷ lệ hình học'}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* MODAL EDIT / CREATE */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-sm font-bold flex items-center gap-2">
              <Box className="h-4 w-4 text-indigo-600" />
              {editingPreset ? "Chỉnh Sửa Mẫu Bao Bì" : "Thêm Mẫu Bao Bì Mới"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-3 py-2 text-xs">
            <div>
              <Label className="text-xs">Mã Mẫu (Code)</Label>
              <Input
                value={formData.code}
                onChange={e => setFormData({ ...formData, code: e.target.value })}
                disabled={!!editingPreset}
                className="h-8 text-xs font-mono mt-1"
              />
            </div>

            <div>
              <Label className="text-xs">Tên Mẫu</Label>
              <Input
                value={formData.name}
                onChange={e => setFormData({ ...formData, name: e.target.value })}
                className="h-8 text-xs mt-1"
              />
            </div>

            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label className="text-[11px] text-slate-500">Dài mặc định (L)</Label>
                <Input
                  type="number"
                  value={formData.length}
                  onChange={e => setFormData({ ...formData, length: Number(e.target.value) })}
                  className="h-8 text-xs mt-1"
                />
              </div>
              <div>
                <Label className="text-[11px] text-slate-500">Rộng mặc định (W)</Label>
                <Input
                  type="number"
                  value={formData.width}
                  onChange={e => setFormData({ ...formData, width: Number(e.target.value) })}
                  className="h-8 text-xs mt-1"
                />
              </div>
              <div>
                <Label className="text-[11px] text-slate-500">Cao mặc định (H)</Label>
                <Input
                  type="number"
                  value={formData.height}
                  onChange={e => setFormData({ ...formData, height: Number(e.target.value) })}
                  className="h-8 text-xs mt-1"
                />
              </div>
            </div>

            <div>
              <Label className="text-xs">Mô Tả Công Thức Trải Phẳng</Label>
              <Input
                value={formData.formulaDesc}
                onChange={e => setFormData({ ...formData, formulaDesc: e.target.value })}
                placeholder="W = 2*(L+W) + 16.6; L = H + W + 25"
                className="h-8 text-xs font-mono mt-1"
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" size="sm" onClick={() => setIsOpen(false)}>
              Hủy
            </Button>
            <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold" onClick={handleSave}>
              Lưu Cấu Hình
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
